import { regularApiClient } from "../utils/kiota-client";
import type { CoolingRegimeTemperatureDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";

export interface CoolingRegimeTemperatureListResult {
  value: CoolingRegimeTemperatureDto[];
  error?: string;
}

export interface CoolingRegimeTemperatureFilters {
  page: number;
  pageSize: number;
  temperatureId?: string;
  description?: string;
  vbnChangeDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

export async function loadCoolingRegimeTemperatures(
  filters: CoolingRegimeTemperatureFilters,
): Promise<CoolingRegimeTemperatureListResult> {
  try {
    const queryConfig = {
      queryParameters: {
        top: filters.pageSize,
        skip: (filters.page - 1) * filters.pageSize,
        filter: buildFilterString(filters),
        orderby: "coolingRegimeTemperatureId asc",
      },
    };

    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeTemperatures.get(queryConfig);

    return {
      value: Array.isArray(response) ? response : [],
    };
  } catch (error) {
    return {
      value: [],
      error: error instanceof Error ? error.message : "Failed to load data",
    };
  }
}

function buildFilterString(
  filters: CoolingRegimeTemperatureFilters,
): string | undefined {
  const conditions: string[] = [];

  if (filters.temperatureId) {
    conditions.push(`coolingRegimeTemperatureId eq ${filters.temperatureId}`);
  }

  if (filters.description) {
    conditions.push(`contains(description, '${filters.description}')`);
  }

  if (filters.vbnChangeDate) {
    conditions.push(`vbnChangeDateTime ge ${filters.vbnChangeDate}`);
  }

  if (filters.modifiedDate) {
    conditions.push(`lastModified ge ${filters.modifiedDate}`);
  }

  if (filters.modifiedBy) {
    conditions.push(`contains(lastModifiedBy, '${filters.modifiedBy}')`);
  }

  return conditions.length > 0 ? conditions.join(" and ") : undefined;
}

/**
 * Fetch all cooling regime temperatures for dropdown options
 */
export async function fetchAllCoolingRegimeTemperatures(): Promise<
  CoolingRegimeTemperatureDto[]
> {
  try {
    const queryConfig = {
      queryParameters: {
        orderby: "coolingRegimeTemperatureId asc",
      },
    };

    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeTemperatures.get(queryConfig);

    return Array.isArray(response) ? response : [];
  } catch (error) {
    return [];
  }
}

/**
 * Create a new cooling regime temperature
 */
export async function createCoolingRegimeTemperature(
  description: string,
  createdBy: string = "System",
): Promise<CoolingRegimeTemperatureDto> {
  const trimmed = (description ?? "").trim();
  if (trimmed.length > 50) {
    throw new Error("De tekst mag maximaal 50 tekens bevatten");
  }
  const now = new Date();
  const response = await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimeTemperatures.post({
      description: trimmed,
      createdBy,
      created: now,
      lastModifiedBy: createdBy,
      lastModified: now,
    });

  if (!response) {
    throw new Error("Failed to create cooling regime temperature");
  }

  return response;
}

/**
 * Update an existing cooling regime temperature
 */
export async function updateCoolingRegimeTemperature(
  id: number,
  description: string,
  createdBy: string,
  created: Date | string | null,
  lastModifiedBy: string = "System",
): Promise<CoolingRegimeTemperatureDto> {
  const trimmed = (description ?? "").trim();
  if (trimmed.length > 50) {
    throw new Error("Description must be at most 50 characters");
  }
  let createdDate: Date;

  if (!created) {
    createdDate = new Date();
  } else if (typeof created === "string") {
    createdDate = new Date(created);
  } else if (created instanceof Date) {
    createdDate = created;
  } else {
    createdDate = new Date(created);
  }

  const payload = {
    coolingRegimeTemperatureId: id,
    description: trimmed,
    createdBy,
    created: createdDate,
    lastModifiedBy,
    lastModified: new Date(),
  };

  await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimeTemperatures.byId(id)
    .put(payload);

  const response = await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimeTemperatures.byId(id)
    .get();

  if (!response) {
    throw new Error("Failed to fetch updated cooling regime temperature");
  }

  return response;
}

/**
 * Delete a cooling regime temperature
 */
export async function deleteCoolingRegimeTemperature(
  id: number,
): Promise<void> {
  await regularApiClient.api
    .vWithVersion("1")
    .coolingRegimeTemperatures.byId(id)
    .delete();
}
